import Lottie from 'lottie-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import {
    hasSeenMobileDesktopRecommendationToday,
    markMobileDesktopRecommendationSeenToday,
} from '@/lib/mobile-desktop-recommendation';
import { isDesktopRecommendationSuppressedRoute, isPatientFacingRoute } from '@/lib/route-patterns';
import { cn } from '@/lib/utils';
import computerAnimation from '@/pages/assets/animations/computer.json';
import { WelcomePage } from '@/pages/constants';
import { createPageUrl } from '@/utils';

const MOBILE_QUERY = '(max-width: 767px)';
const EXIT_DURATION_MS = 500;

const isMobileViewport = () => {
    if (typeof window === 'undefined') return false;

    return window.matchMedia(MOBILE_QUERY).matches;
};

export function MobileDesktopRecommendation() {
    const location = useLocation();
    const { user, status, isLoading } = useAuth();
    const exitTimeoutRef = useRef<number | null>(null);
    const hasDecidedRef = useRef(false);
    const [isMobile, setIsMobile] = useState(isMobileViewport);
    const [isVisible, setIsVisible] = useState(false);
    const [isExiting, setIsExiting] = useState(false);

    // `status === 'active'` rather than `isAuthenticated`, which is also true for
    // pending_verification / blocked_conflict — those users sit on their own
    // full-screen pages. The welcome flag mirrors the router's own WelcomeGate;
    // the extra path check covers the render window where the flag is already
    // true in the query cache but WelcomePage has not navigated away yet.
    const canShow =
        !isLoading &&
        status === 'active' &&
        user?.welcome_screen_completed === true &&
        location.pathname !== createPageUrl(WelcomePage) &&
        !isPatientFacingRoute(location.pathname) &&
        !isDesktopRecommendationSuppressedRoute(location.pathname);

    const clearExitTimeout = useCallback(() => {
        if (exitTimeoutRef.current === null) return;

        window.clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
    }, []);

    const handleDismiss = useCallback(() => {
        if (exitTimeoutRef.current !== null) return;

        setIsExiting(true);
        exitTimeoutRef.current = window.setTimeout(() => {
            setIsVisible(false);
            setIsExiting(false);
            exitTimeoutRef.current = null;
        }, EXIT_DURATION_MS);
    }, []);

    useEffect(() => clearExitTimeout, [clearExitTimeout]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQuery = window.matchMedia(MOBILE_QUERY);
        const handleChange = () => setIsMobile(mediaQuery.matches);

        handleChange();
        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    // Taken once per mount: marking the recommendation as seen must not feed back
    // into this effect, and StrictMode runs it twice in development.
    useEffect(() => {
        if (hasDecidedRef.current || !canShow || !isMobile) return;

        hasDecidedRef.current = true;

        if (hasSeenMobileDesktopRecommendationToday()) return;

        // Só mostra o que conseguiu registrar: sem uma storage que aceite a
        // marca do dia, o overlay voltaria a cada reload.
        if (!markMobileDesktopRecommendationSeenToday()) return;

        setIsVisible(true);
    }, [canShow, isMobile]);

    // Retires the overlay as soon as it stops being eligible: rotating a phone to
    // landscape clears the 767px breakpoint, and browser-history navigation can
    // land on a patient-facing route while it is still on screen. `hasDecidedRef`
    // stays set on purpose — the day was already marked when it appeared.
    useEffect(() => {
        if (isMobile && canShow) return;

        clearExitTimeout();
        setIsVisible(false);
        setIsExiting(false);
    }, [canShow, clearExitTimeout, isMobile]);

    if (!isVisible) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-desktop-recommendation-title"
            aria-describedby="mobile-desktop-recommendation-description"
            className={cn(
                'fixed inset-0 z-[1200] flex min-h-dvh overflow-hidden bg-[#FAFBFF] px-6 py-16 text-[#223564] sm:px-8 lg:px-10',
                'transition-opacity duration-500 ease-out',
                isExiting ? 'opacity-0' : 'opacity-100',
            )}
        >
            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                <div className="absolute -left-56 -top-64 size-[44rem] rounded-full bg-[#DCE3FF]/70 blur-3xl" />
                <div className="absolute -bottom-56 -right-48 size-[40rem] rounded-full bg-[#E9DFFF]/70 blur-3xl" />
                <div className="absolute right-[18%] top-[30%] size-[30rem] rounded-full bg-[#FFE3D1]/50 blur-3xl" />
                <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-white/80" />
            </div>

            <div
                className={cn(
                    'relative z-10 mx-auto flex w-full max-w-xl flex-col justify-center gap-6 duration-300',
                    'transition-[opacity,transform,filter] duration-500 ease-out',
                    isExiting
                        ? 'translate-y-3 scale-[0.985] opacity-0 blur-sm'
                        : 'translate-y-0 scale-100 opacity-100 blur-0',
                )}
            >
                <div aria-hidden="true" className="size-56 self-start">
                    <Lottie animationData={computerAnimation} loop={false} className="size-full" />
                </div>

                <h2
                    id="mobile-desktop-recommendation-title"
                    className="text-balance text-2xl font-semibold leading-tight tracking-[-0.02em] text-[#00006A]"
                >
                    O LaudoPsi fica ainda melhor no computador
                </h2>

                <div
                    id="mobile-desktop-recommendation-description"
                    className="space-y-4 text-pretty text-base leading-8 text-[#5F7298]"
                >
                    <p>
                        Para editar laudos, visualizar páginas A4 e organizar instrumentos com mais
                        conforto, recomendamos usar a plataforma no desktop.
                    </p>
                    <p>No celular, você ainda pode acessar normalmente 😊</p>
                </div>

                <Button
                    type="button"
                    variant="link"
                    size="lg"
                    className="h-auto self-start p-0 text-base font-semibold text-[#00006A] underline underline-offset-4"
                    onClick={handleDismiss}
                >
                    Entendi
                </Button>
            </div>
        </div>
    );
}
